/**
 * Shared formatting helpers for the regional label-version naming.
 *
 * Used by VersionSelector (the main version dropdown) and DrugDetailPage's
 * Compare dropdown so the two render the same string for any given
 * (region, version) pair.  Centralised here so adding / renaming a region
 * means editing this file only.
 */

/**
 * Region-aware label prefix. Each regulator's preferred terminology:
 *   US → "USPI"                  (FDA United States Prescribing Information)
 *   EU → "EU SmPC"               (EMA Summary of Product Characteristics)
 *   UK → "UK SmPC"               (MHRA Summary of Product Characteristics)
 *   CA → "CA Product Monograph"  (Health Canada Product Monograph)
 *   AU → "AU PI"                 (TGA Product Information)
 *   anything else (or empty)     → "EU SmPC" as the catch-all default for
 *     legacy / unknown regions, matches the prior implementation behaviour.
 *
 * The table below is the single source of truth — extend it when a new
 * region is added.
 */
const REGION_LABEL_PREFIX: Record<string, string> = {
  US: 'USPI',
  US_FDA: 'USPI (Drugs@FDA)',
  EU: 'EU SmPC',
  UK: 'UK SmPC',
  CA: 'CA Product Monograph',
  AU: 'AU PI',
  CN: 'NMPA 说明书',
};

export const labelPrefixForRegion = (region?: string | null): string => {
  const key = (region || '').toUpperCase();
  return REGION_LABEL_PREFIX[key] ?? 'EU SmPC';
};

const MONTH_ABBREV = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format an ISO date string (YYYY-MM-DD or full ISO timestamp) as
 * "MMM DD YYYY" (e.g. "Jan 02 2025").  Returns empty string for null /
 * undefined / unparseable input so callers can fall back to a
 * date-less label without rendering an awkward trailing dash.
 */
export const formatVersionDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mmm = MONTH_ABBREV[d.getUTCMonth()];
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mmm} ${dd} ${yyyy}`;
};

/**
 * Build the final label string for an FDA-current or FDA-historical
 * version. Format: "${prefix} - V${versionNumber} - ${MMM DD YYYY}".
 * Each ` - ${segment}` is omitted independently when the corresponding
 * value isn't available, so a missing date or version number degrades
 * gracefully (no awkward trailing dashes, no "V" / "Vundefined").
 */
export const formatRegionalVersionLabel = (
  region: string | null | undefined,
  effectiveDate: string | null | undefined,
  versionNumber?: number | null | undefined,
): string => {
  const prefix = labelPrefixForRegion(region);
  const parts: string[] = [prefix];
  if (versionNumber != null && Number.isFinite(versionNumber)) {
    parts.push(`V${versionNumber}`);
  }
  const date = formatVersionDate(effectiveDate);
  if (date) parts.push(date);
  return parts.join(' - ');
};
